/**
 * Hook that derives the full sidebar state from the current URL.
 *
 * 1. Matches the pathname against the ordered level configs.
 * 2. Extracts route params (:clientId, :locationId, :cageId).
 * 3. Computes full URLs for each sidebar item.
 * 4. Determines which item is active.
 * 5. Splits items into regular vs static sections.
 * 6. Interpolates the back-button URL template with actual params.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, type Params, useLocation } from 'react-router-dom';

import type { IconComponent } from '@/components/atoms/SvgIcon/SvgIcon';
import { useAuth } from '@/contexts/auth';
import { cageScopedSearch } from '@/contexts/cage-filter-context';
import { useDemoMode } from '@/contexts/demo/DemoContext';
import { useLocationDashboardUrl } from '@/hooks/useLocationDashboardUrl';
import { getDemoDisplayName } from '@/utils/demoMasking';

import type {
    SidebarItemConfig,
    SidebarLevel,
    SidebarLevelConfig,
    VisibilityConfig,
} from './sidebarConfig';
import { sidebarLevels } from './sidebarConfig';

export interface SidebarNavItem {
    id: string;
    label: string;
    highlightLabelKey?: string;
    /** Fully-resolved URL (e.g. '/c/42/lice-report') */
    fullUrl: string;
    icon?: IconComponent;
    isActive: boolean;
    isStatic: boolean;
    children?: SidebarNavItem[];
    /** When true, shows a right-pointing chevron indicating navigation to a sub-level sidebar */
    navigatesToSubLevel?: boolean;
    /** Declarative visibility conditions (passed through from config) */
    visibility?: VisibilityConfig;
}

export interface SidebarBackOption {
    title: string;
    url: string;
}

export interface SidebarNavigationResult {
    level: SidebarLevel | null;
    items: SidebarNavItem[];
    staticItems: SidebarNavItem[];
    backOption: SidebarBackOption | null;
    /** Extracted route params (e.g. { clientId: '42', locationId: '7' }) */
    params: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface LevelMatch {
    config: SidebarLevelConfig;
    params: Record<string, string>;
}

function definedParams(params: Params): Record<string, string> {
    const defined: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) {
            defined[key] = value;
        }
    }
    return defined;
}

/**
 * Find the first level config whose pattern matches the pathname.
 * Levels are ordered most-specific-first in sidebarConfig.
 */
function findMatchingLevel(pathname: string): LevelMatch | null {
    for (const config of sidebarLevels) {
        const match = matchPath({ path: config.matchPattern, end: false }, pathname);

        if (match) {
            return { config, params: definedParams(match.params) };
        }
    }
    return null;
}

/**
 * Replace :param placeholders in a URL template with actual values.
 *
 * e.g. interpolateParams('/c/:clientId/location/:locationId', { clientId: '42', locationId: '7' })
 *      → '/c/42/location/7'
 */
function interpolateParams(template: string, params: Record<string, string>): string {
    return template.replace(/:([a-zA-Z_]+)/g, (_, key) => params[key] ?? `:${key}`);
}

/**
 * Build the URL prefix from a match pattern and params.
 * Reuses interpolateParams to resolve the pattern into a concrete path.
 */
function buildPrefix(matchPattern: string, params: Record<string, string>): string {
    return interpolateParams(matchPattern, params);
}

/**
 * Determine whether a sidebar item is active based on the current pathname.
 */
function isItemActive(
    pathname: string,
    itemUrl: string,
    exact?: boolean,
    activePathPrefixes: string[] = []
): boolean {
    if (activePathPrefixes.some((prefix) => pathname.startsWith(prefix))) {
        return true;
    }

    if (exact) {
        // For items with path '' (root), the itemUrl equals the prefix.
        // We need exact match or match with trailing slash.
        return pathname === itemUrl || pathname === `${itemUrl}/`;
    }
    return pathname.startsWith(itemUrl);
}

/**
 * Map a SidebarItemConfig to a SidebarNavItem with resolved URL and active state.
 */
function mapItem(
    item: SidebarItemConfig,
    prefix: string,
    pathname: string,
    search = ''
): SidebarNavItem {
    // Two values, on purpose. `itemPath` answers "is this the page we are on",
    // which is a pathname question — comparing against a URL with a query string
    // would make every item inactive the moment a filter is set.
    const itemPath = prefix + item.path;
    const fullUrl = itemPath + search;
    const activePathPrefixes =
        item.activePathPrefixes?.map((activePath) => prefix + activePath) ?? [];

    const children = item.children?.map((child) => mapItem(child, prefix, pathname, search));

    const isChildActive = children?.some((c) => c.isActive) ?? false;
    const isActive =
        isChildActive || isItemActive(pathname, itemPath, item.exact, activePathPrefixes);

    return {
        id: item.id,
        label: item.label,
        highlightLabelKey: item.highlightLabelKey,
        fullUrl,
        icon: item.icon,
        isActive,
        isStatic: item.isStatic ?? false,
        children: children && children.length > 0 ? children : undefined,
        navigatesToSubLevel: item.navigatesToSubLevel,
        visibility: item.visibility,
    };
}

export function useSidebarNavigation(): SidebarNavigationResult {
    const { pathname, search } = useLocation();
    const { clients } = useAuth();
    const { isDemoMode } = useDemoMode();
    const { t } = useTranslation();

    // Resolved outside the memo so useLocationDashboardUrl can be called unconditionally
    // (hooks cannot run inside useMemo or behind the cage-level branch).
    const match = useMemo(() => findMatchingLevel(pathname), [pathname]);
    const preferredLocationUrl = useLocationDashboardUrl(
        match?.params.clientId ? Number(match.params.clientId) : 0,
        match?.params.locationId ? Number(match.params.locationId) : 0
    );

    return useMemo(() => {
        if (!match) {
            return { level: null, items: [], staticItems: [], backOption: null, params: {} };
        }

        const { config, params } = match;
        const prefix = buildPrefix(config.matchPattern, params);

        // Filters are cage-scoped, so they travel only between pages of the same
        // cage. Keyed on `cageId` rather than on the level name so any level that
        // sits under a cage gets it, and nothing above one does — the back option
        // below leaves the cage entirely and is built without it.
        const itemSearch = params.cageId != null ? cageScopedSearch(search) : '';

        // Map all config items to nav items with resolved URLs and active state
        const allItems = config.items.map((item) => mapItem(item, prefix, pathname, itemSearch));

        const items = allItems.filter((item) => !item.isStatic);
        const staticItems = allItems.filter((item) => item.isStatic);

        // Build back option: resolve title from auth for location (client name) and cage (location name)
        const clientId = params.clientId ? Number(params.clientId) : undefined;
        const locationId = params.locationId ? Number(params.locationId) : undefined;
        const client = clientId != null ? clients.find((c) => c.id === clientId) : undefined;
        const location =
            client != null && locationId != null
                ? client.locations?.find((l) => l.id === locationId)
                : undefined;

        let backTitle = config.back?.title ?? '';
        if (isDemoMode) {
            if (config.level === 'location') {
                backTitle = getDemoDisplayName(client?.id ?? 0, 'client', t);
            } else if (config.level === 'cage') {
                backTitle = getDemoDisplayName(location?.id ?? 0, 'location', t);
            }
        } else if (config.back) {
            if (config.level === 'location') {
                backTitle = client?.name ?? config.back.title;
            } else if (config.level === 'cage') {
                backTitle = location?.name ?? config.back.title;
            }
            // wounds: keep config.back.title
        }

        // Cage → location back uses the preferred dashboard URL; other levels keep urlTemplate.
        const backOption: SidebarBackOption | null = config.back
            ? {
                  title: backTitle,
                  url:
                      config.level === 'cage'
                          ? preferredLocationUrl
                          : interpolateParams(config.back.urlTemplate, params),
              }
            : null;

        return { level: config.level, items, staticItems, backOption, params };
        // `search` matters: the links carry the current filters, so they have to be
        // rebuilt when a filter changes, not only when the path does.
    }, [pathname, search, clients, isDemoMode, t, match, preferredLocationUrl]);
}
